import React, { useState, useEffect, useCallback } from "react";
import { saveKGColumn } from "services/DataModelService";
import { Header } from "build-ts/metadata";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { AggregationType, ColumnType, TimeBucket } from "build-ts/enums";
import showToast from "hooks/useCustomToast";
import KGColumnCreationFormComponent from "../components/KGColumnCreationForm";

interface KGColumnCreationFormProps {
  defaultName?: string;
  onClose: () => void;
  colType: ColumnType;
  refreshKGColumns: () => Promise<Header[]>;
  onSuccess?: (createdHeader: Header) => void;
}

const KGColumnCreationForm: React.FC<KGColumnCreationFormProps> = ({
  defaultName,
  onClose,
  colType,
  refreshKGColumns,
  onSuccess,
}) => {
  const [name, setName] = useState(defaultName ?? "");
  const [description, setDescription] = useState("");
  /** Low cardinality → 1, High → 0 */
  const [lowCardinality, setLowCardinality] = useState(true);
  const [timeBucket, setTimeBucket] = useState<TimeBucket>(TimeBucket.DAILY);
  const [aggregation, setAggregation] = useState<AggregationType>(AggregationType.SUM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form whenever the target column type or default name changes
  useEffect(() => {
    setName(defaultName ?? "");
    setDescription("");
    setLowCardinality(true);
    setTimeBucket(TimeBucket.DAILY);
    setAggregation(AggregationType.SUM);
  }, [defaultName, colType]);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      showToast("Name is required", "", "error");
      return;
    }

    setIsSubmitting(true);

    const kgColumnProto = KGColumnProto.create({
      header: Header.create({ name, description }),
      column_type: colType,
    });

    if (colType === ColumnType.ATTRIBUTE) {
      kgColumnProto.cardinality = lowCardinality ? 1 : 0;
    } else if (colType === ColumnType.MEASURE) {
      kgColumnProto.aggregation = aggregation;
    } else if (colType === ColumnType.TEMPORAL) {
      kgColumnProto.time_bucket = timeBucket;
    }

    const result = await saveKGColumn(kgColumnProto, false);
    if (result) {
      await refreshKGColumns();
      showToast("KG Column created successfully", "", "success");
      onSuccess?.(result.header!);
      setIsSubmitting(false);
      onClose();
    } else {
      setIsSubmitting(false);
    }
  }, [name, description, colType, lowCardinality, aggregation, timeBucket, refreshKGColumns, onSuccess, onClose]);

  return (
    <KGColumnCreationFormComponent
      colType={colType}
      name={name}
      description={description}
      lowCardinality={lowCardinality}
      timeBucket={timeBucket}
      aggregation={aggregation}
      isSubmitting={isSubmitting}
      onNameChange={setName}
      onDescriptionChange={setDescription}
      onLowCardinalityChange={setLowCardinality}
      onTimeBucketChange={setTimeBucket}
      onAggregationChange={setAggregation}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
};

export default KGColumnCreationForm;
